/**
 * JornadaAutor - Página da Jornada do Autor
 * 
 * "Esta tela NÃO é um relatório. É um espelho."
 * "Se o usuário analisar → falhou. Se sentir → acertou."
 * 
 * Features:
 * - Linha do tempo horizontal com snap
 * - Cards de fases/períodos
 * - Insights flutuantes
 * - Comparação ANTES vs AGORA
 * - Cofre de Identidade (inferior esquerdo)
 * - Pulso Criativo (inferior direito)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';

// Components
import TimelineContainer from '../components/jornada/TimelineContainer';
import InsightOverlay from '../components/jornada/InsightOverlay';
import CompareView from '../components/jornada/CompareView';
import IdentityVaultIndicator from '../components/jornada/IdentityVaultIndicator';
import CreativePulseIndicator from '../components/jornada/CreativePulseIndicator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sample insights that appear between cards
const INSIGHTS = [
  'Aqui sua escrita ficou mais fluida',
  'Você começou a aprofundar emoções',
  'Seu ritmo ficou mais envolvente',
  'A confiança na sua voz aumentou',
  'Você encontrou seu estilo',
  'Suas descrições ganharam cor',
  'O diálogo ficou mais natural',
];

export default function JornadaAutor() {
  const navigate = useNavigate();
  
  // Data states
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vaultProgress, setVaultProgress] = useState(0);
  const [pulseState, setPulseState] = useState('flow');
  
  // UI states
  const [activePhaseIndex, setActivePhaseIndex] = useState(0);
  const [currentInsight, setCurrentInsight] = useState('');
  const [showInsight, setShowInsight] = useState(false);
  const [comparePhase, setComparePhase] = useState(null);
  const [showCompare, setShowCompare] = useState(false);

  // Fetch journey data
  useEffect(() => {
    fetchJourneyData();
  }, []);

  const fetchJourneyData = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/author/journey`,
        { withCredentials: true }
      );
      
      if (response.data) {
        setPhases(response.data.phases || []);
        setVaultProgress(response.data.vaultProgress || 0);
        setPulseState(response.data.pulseState || 'flow');
      }
    } catch (error) {
      // Fallback to sample data when API not available
      setPhases(getSamplePhases());
      setVaultProgress(45);
      setPulseState('flow');
    } finally {
      setLoading(false);
    }
  };

  // Handle phase change - show random insight
  const handlePhaseChange = useCallback((index) => {
    setActivePhaseIndex(index);
    
    // Show insight with some randomness
    if (Math.random() > 0.4 && index > 0) {
      const randomInsight = INSIGHTS[Math.floor(Math.random() * INSIGHTS.length)];
      setCurrentInsight(randomInsight);
      setShowInsight(true);
      
      // Hide after 3 seconds
      setTimeout(() => setShowInsight(false), 3000);
    } else {
      setShowInsight(false);
    }
  }, []);

  // Handle compare click
  const handleCompare = (phase) => {
    setComparePhase(phase);
    setShowCompare(true);
  };

  // Get current phase for comparison
  const getCurrentPhase = () => {
    if (phases.length === 0) return null;
    return phases[phases.length - 1];
  };

  // Navigate to Cofre de Identidade
  const handleVaultClick = () => {
    navigate('/cofre-identidade');
  };

  // Navigate to detailed pulse view
  const handlePulseClick = () => {
    // TODO: Navigate to detailed creative pulse page
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(180deg, #f5f0e8 0%, #ebe6dc 100%)' }}
      >
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-amber-200 border-t-amber-500 animate-spin" />
          <p className="text-stone-500 italic">Carregando sua jornada...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(180deg, #ebe6dc 0%, #ddd5c8 50%, #d0c8bb 100%)',
      }}
      data-testid="jornada-autor-page"
    >
      {/* Subtle parallax background texture */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000' fill-opacity='0.1'%3E%3Cpath d='M0 0h1v1H0zM2 0h1v1H2zM4 0h1v1H4zM1 1h1v1H1zM3 1h1v1H3zM5 1h1v1H5zM0 2h1v1H0zM2 2h1v1H2zM4 2h1v1H4zM1 3h1v1H1zM3 3h1v1H3zM5 3h1v1H5zM0 4h1v1H0zM2 4h1v1H2zM4 4h1v1H4zM1 5h1v1H1zM3 5h1v1H3zM5 5h1v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full bg-white/80 hover:bg-white text-stone-600 hover:text-stone-800 transition-colors shadow-sm"
          aria-label="Voltar"
        >
          <ArrowLeft size={20} />
        </button>

        <div className="text-center flex-1">
          <h1 className="text-xl md:text-2xl font-serif text-stone-800">
            Jornada do Autor
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Veja como sua escrita evoluiu ao longo do tempo
          </p>
        </div>

        {/* Spacer for centering */}
        <div className="w-10" />
      </header>

      {/* Timeline Container */}
      <TimelineContainer
        phases={phases}
        onCompare={handleCompare}
        onPhaseChange={handlePhaseChange}
      />

      {/* Insight Overlay */}
      <InsightOverlay
        insight={currentInsight}
        visible={showInsight}
      />

      {/* Compare View Modal */}
      <CompareView
        pastPhase={comparePhase}
        currentPhase={getCurrentPhase()}
        isOpen={showCompare}
        onClose={() => setShowCompare(false)}
      />

      {/* Cofre de Identidade (bottom left) */}
      <IdentityVaultIndicator
        progress={vaultProgress}
        onClick={handleVaultClick}
      />

      {/* Pulso Criativo (bottom right) */}
      <CreativePulseIndicator
        state={pulseState}
        onClick={handlePulseClick}
      />

      {/* Footer quote */}
      <footer className="relative z-10 text-center py-6">
        <p className="text-xs text-stone-400 italic">
          O tempo não passou. Ele foi escrito por você.
        </p>
      </footer>
    </div>
  );
}

// Sample data for demonstration
function getSamplePhases() {
  return [
    {
      id: '1',
      period: '2023',
      title: 'Os Primeiros Passos',
      writingStyle: 'Escrita exploratória, buscando encontrar sua voz. Frases mais longas, descrições detalhadas.',
      thought: 'Ainda descobrindo o que quero dizer ao mundo.',
      excerpt: 'O sol se punha lentamente no horizonte, tingindo o céu de tons alaranjados e roxos, enquanto ela observava pela janela, perdida em pensamentos que não conseguia nomear...',
    },
    {
      id: '2',
      period: '2024 - Início',
      title: 'Encontrando o Ritmo',
      writingStyle: 'Mais confiante, frases mais curtas e impactantes. Diálogos começam a fluir naturalmente.',
      thought: 'Estou começando a entender minha própria voz.',
      excerpt: 'Ela respirou fundo. Não havia mais tempo para dúvidas. A porta estava ali, esperando. E ela sabia — sempre soube — que precisava atravessá-la.',
    },
    {
      id: '3',
      period: '2024 - Meio',
      title: 'A Voz Amadurece',
      writingStyle: 'Equilíbrio entre emoção e ação. Metáforas mais sutis e eficazes.',
      thought: 'Cada palavra agora carrega intenção.',
      excerpt: 'O silêncio entre eles dizia mais que mil conversas. Era um silêncio cheio, pesado de coisas não ditas, de perguntas engolidas, de respostas que nunca viriam.',
    },
    {
      id: '4',
      period: '2025',
      title: 'Sua Voz Única',
      writingStyle: 'Estilo próprio consolidado. Narrativa fluida, emocionalmente ressonante.',
      thought: 'Finalmente escrevo como respiro — naturalmente.',
      excerpt: 'Não era sobre chegar a algum lugar. Nunca foi. Era sobre o caminho, sobre cada passo dado no escuro, confiando que o próximo estaria lá. E estava.',
    },
  ];
}
